import React, { useCallback, useMemo, useState } from "react";
import { connect } from "react-redux";

import { faAngleDown } from "@fortawesome/pro-solid-svg-icons/faAngleDown";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import Popover from "antd/es/popover";
import { useIntl } from "react-intl";

import Team from "@mapmycustomers/shared/types/Team";
import User from "@mapmycustomers/shared/types/User";
import { Button } from "@mapmycustomers/ui";

import UsersSelect from "@app/component/UsersSelect";
import OrgOptions, { getOrgOptionsDisplayName } from "@app/scene/reports/enums/OrgOptions";
import { setUserIds } from "@app/scene/reports/store/actions";
import { getUserIds } from "@app/scene/reports/store/selectors";
import { isCurrentUserMember, isCurrentUserOwner } from "@app/store/iam";
import { isOwner } from "@app/store/iam/util";
import { getTeams, getUsers } from "@app/store/members";
import { RootState } from "@app/store/rootReducer";
import { userDisplayName } from "@app/util/formatters";
import getSelectedTeams from "@app/util/members/getSelectedTeams";

import styles from "./OwnerManagerMenu.module.scss";

interface Props {
  isCurrentUserMember: boolean;
  isCurrentUserOwner: boolean;
  onSelectUsersByIds: (userIds: User["id"][]) => void;
  teams: Team[];
  userIds?: User["id"][];
  users: User[];
}

const OwnerManagerMenu: React.FC<Props> = ({
  isCurrentUserMember,
  isCurrentUserOwner,
  onSelectUsersByIds,
  teams,
  userIds = [],
  users,
}) => {
  const intl = useIntl();

  const [popoverVisible, setPopoverVisibility] = useState<boolean>(false);
  const handleHide = useCallback(() => setPopoverVisibility(false), []);

  // We only use userIds to calculate the label. We don't want it to change until Apply button is pressed.
  const label = useMemo(() => {
    if (isCurrentUserOwner && userIds.length === users.length) {
      return getOrgOptionsDisplayName(intl, OrgOptions.ENTIRE_ORG);
    } else if (userIds.length === 1) {
      return userDisplayName(users.find(({ id }) => id === userIds[0])!);
    }

    const selectedTeams = getSelectedTeams(teams, userIds);
    // all selected users belong to a single team
    const isOnlyASingleTeamSelected =
      selectedTeams.length === 1 &&
      userIds.every((id) => selectedTeams[0].users.some((user) => user.id === id));

    if (isOnlyASingleTeamSelected) {
      return selectedTeams[0].name;
    } else {
      return intl.formatMessage({
        id: "reports.userMenu.title.multipleUsers",
        defaultMessage: "Multiple Users or Teams",
        description:
          "Label of the User Menu on report pages when there is more than one team or user selected",
      });
    }
  }, [intl, userIds, users, isCurrentUserOwner, teams]);

  const filteredUsers = useMemo(
    () => users.filter((user) => (isOwner(user) ? isCurrentUserOwner : true)),
    [isCurrentUserOwner, users]
  );

  const handleApply = useCallback(
    (userIds: User["id"][]) => {
      onSelectUsersByIds(userIds);
      handleHide();
    },
    [handleHide, onSelectUsersByIds]
  );

  if (isCurrentUserMember) {
    return null;
  }

  return (
    <Popover
      content={
        <UsersSelect
          isCurrentUserOwner={isCurrentUserOwner}
          onApply={handleApply}
          onCancel={handleHide}
          teams={teams}
          userIds={userIds}
          users={filteredUsers}
        />
      }
      destroyTooltipOnHide
      onOpenChange={setPopoverVisibility}
      open={popoverVisible}
      overlayClassName={styles.container}
      trigger={["click"]}
    >
      <Button
        className={styles.triggerButton}
        rightIcon={<FontAwesomeIcon icon={faAngleDown} />}
        type="link"
      >
        {label}
      </Button>
    </Popover>
  );
};

const mapStateToProps = (state: RootState) => ({
  isCurrentUserMember: isCurrentUserMember(state),
  isCurrentUserOwner: isCurrentUserOwner(state),
  teams: getTeams(state),
  userIds: getUserIds(state),
  users: getUsers(state),
});

const mapDispatchToProps = {
  onSelectUsersByIds: setUserIds,
};

export default connect(mapStateToProps, mapDispatchToProps)(OwnerManagerMenu);
